/**
 * Signing in — the account, and the honest account of what an account is for.
 *
 * Paint 6000 works with no account and that does not change. Every sheet, every course, every log
 * entry and every figure on the profile lives in this browser and is reached with no network at
 * all. Signing in buys exactly one thing: a team whose join code resolves on somebody else's
 * machine. So nothing in this file is on the path to first paint, nothing here is awaited before
 * a sheet opens, and no route in the app is guarded by it.
 *
 * ── Why email and a password, and not a magic link ─────────────────────────────────────────
 *
 * Measured against this project on 2026-09-01, not assumed:
 *
 *   GET  /auth/v1/settings → { "external": { "email": true, "anonymous_users": false },
 *                              "disable_signup": false, "mailer_autoconfirm": false }
 *   POST /auth/v1/token?grant_type=password → 400 invalid_credentials   (not "provider disabled")
 *   POST /auth/v1/signup   (no body)        → 422 anonymous_provider_disabled
 *
 * The password grant is live: a person who has an account can sign in with one round trip and no
 * mail at all. A magic link is the opposite — it is one hundred per cent dependent on outbound
 * mail, and this project has no custom SMTP, so its built-in sender is rate-limited to a couple
 * of messages an hour and only to addresses on the project team. A sign-in method that cannot
 * deliver its own credential is a wall with a spinner in front of it.
 *
 * `mailer_autoconfirm: false` is the other half of the picture and the app says so out loud:
 * CREATING an account still needs a confirmation mail, so account creation is the part that can
 * fail here. Sign-in is not, and the two are reported separately rather than as one "auth error".
 *
 * Anonymous sign-in stays a live code path in `teamnet.ts` — the moment the dashboard toggle is
 * flipped, `anonymous_users` reads `true` here and nothing else has to change.
 *
 * ── Credentials ────────────────────────────────────────────────────────────────────────────
 *
 * `VITE_SUPABASE_URL` and `VITE_SUPABASE_PUBLISHABLE_KEY` are read through `supabase.ts` and, for
 * the settings probe, from `import.meta.env` directly. Neither is ever put in a log line, an
 * error message, or a rendered string. That is also why no raw exception text reaches the UI:
 * a thrown `fetch` carries the request URL, and the request URL carries the key.
 *
 * ── Guest work survives getting an account, and it is structural ───────────────────────────
 *
 * A guest who later creates an account keeps everything already on the device. That is not a
 * migration step somebody has to remember to run; it is a property of where the work lives:
 * IndexedDB `euclid`, keyed by sheet id, project id and log-entry id, with no account anywhere
 * in a key or an index. Gaining a session adds a row to `localStorage`; it does not open a
 * different database.
 *
 * What could break it is this module growing the ability to clear anything, so it is fenced
 * rather than trusted: nothing here imports `storage`, `workspace` or `profile`, and nothing
 * here names `indexedDB`, `deleteDatabase` or `clear`. `tests/auth.test.ts` reads this file and
 * fails the build if any of that stops being true. The one thing sign-in DOES write outside the
 * session is the entry choice — `rememberEntry`, which sets one string in `localStorage` —
 * promoted from guest to account on the same device. A relabel, never a reset.
 */

import { computed, ref } from 'vue';
import type { Session, User } from '@supabase/supabase-js';

import { rememberEntry } from './settings';
import { getSupabase, supabaseConfigured } from './supabase';

// ── What the server says it supports ───────────────────────────────────────────────────────

export interface Providers {
  /** Email + password sign-in is switched on. */
  email: boolean;
  /** Anonymous sign-in is switched on. `false` here is why teams fall back to this device. */
  anonymous: boolean;
  /** New accounts are allowed at all. */
  signups: boolean;
  /** A new account must confirm its address by mail before it has a session. */
  confirmation: boolean;
}

export type Probe =
  | { ok: true; providers: Providers }
  | { ok: false; reason: 'no-credentials' | 'unreachable' };

/**
 * The shape `GET /auth/v1/settings` answers with, narrowed to what is actually read.
 *
 * It is a public endpoint — it takes the publishable key and returns which providers a project
 * has enabled — which is what makes an honest "this provider is switched off" possible without
 * making a person attempt a sign-in to find out.
 */
interface SettingsPayload {
  external?: Record<string, boolean>;
  disable_signup?: boolean;
  mailer_autoconfirm?: boolean;
}

/** Pure, so the mapping is testable without a network. */
export const readProviders = (payload: SettingsPayload): Providers => ({
  email: payload.external?.email === true,
  anonymous: payload.external?.anonymous_users === true,
  signups: payload.disable_signup !== true,
  confirmation: payload.mailer_autoconfirm !== true,
});

let probed: Probe | null = null;

/**
 * Ask the server what it supports.
 *
 * ── A success is cached; a FAILURE is not ──────────────────────────────────────────────────
 *
 * A project's providers do not change while somebody is looking at a form, so the answer is kept
 * and re-asking on every keystroke would be a request per character. That reasoning holds for an
 * answer. It does not hold for a failure, and treating the two the same was a real fault: one
 * slow response — a laptop waking, a moment of wifi, anything over the eight-second limit — pinned
 * *"The server is not answering, so signing in is unavailable"* on the gate for the life of the
 * tab, with the server perfectly healthy a second later and no way back but a reload.
 *
 * So a failure is remembered only long enough not to hammer the endpoint, and any press after
 * that asks again. `RETRY_MS` is short because the person reading that sentence is standing there
 * deciding whether the app is broken.
 */
const RETRY_MS = 3000;
let failedAt = 0;

export async function probeProviders(): Promise<Probe> {
  if (probed?.ok) return probed;
  if (probed && Date.now() - failedAt < RETRY_MS) return probed;
  const url = (import.meta.env.VITE_SUPABASE_URL ?? '').trim();
  const key = (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? '').trim();
  // No credentials is a fact about the BUILD and cannot change while it is running, so it is the
  // one failure worth keeping for good.
  if (!url || !key) { probed = { ok: false, reason: 'no-credentials' }; return probed; }

  try {
    const response = await fetch(`${url}/auth/v1/settings`, {
      headers: { apikey: key },
      signal: AbortSignal.timeout(8000),
    });
    if (!response.ok) {
      failedAt = Date.now();
      probed = { ok: false, reason: 'unreachable' };
      return probed;
    }
    probed = { ok: true, providers: readProviders(await response.json() as SettingsPayload) };
  } catch {
    // Never inspect the exception: it carries the request URL and the URL carries the key.
    failedAt = Date.now();
    probed = { ok: false, reason: 'unreachable' };
  }
  return probed;
}

/**
 * One sentence naming what is off and what that means, or `null` when nothing is.
 *
 * "A provider is disabled" is a setting rather than a fault, and the difference matters: it
 * points at a dashboard toggle instead of at the code. Pure, so the wording is under test.
 */
export function providerNote(probe: Probe): string | null {
  if (!probe.ok) {
    return probe.reason === 'no-credentials'
      ? 'This build has no server, so there is nothing to sign in to.'
      : 'The server is not answering, so signing in is unavailable.';
  }
  const { email, signups, confirmation } = probe.providers;
  if (!email) return 'Email sign-in is switched off in this Supabase project.';
  if (!signups) return 'This project is not accepting new accounts.';
  if (confirmation) {
    return 'A new account needs a confirmation mail, and this project’s mailer sends a couple an hour.';
  }
  return null;
}

// ── The session ────────────────────────────────────────────────────────────────────────────

export interface Account {
  id: string;
  /** The address, for "signed in as". Anonymous users have none. */
  email: string;
  anonymous: boolean;
}

const current = ref<Account | null>(null);
const checking = ref(false);
const restored = ref(false);

const toAccount = (user: User): Account => ({
  id: user.id,
  email: user.email ?? '',
  anonymous: user.is_anonymous === true,
});

export const account = computed(() => current.value);
export const signedIn = computed(() => current.value !== null);
export const checkingSession = computed(() => checking.value);
export const sessionKnown = computed(() => restored.value);

let watching = false;

/**
 * Pick up a session that is already on this device.
 *
 * Idempotent and always settles. With no credentials it resolves immediately having decided
 * "signed out", because the alternative — a promise that waits on a client that will never be
 * built — is the spinner this whole file exists to avoid.
 */
export async function restore(): Promise<Account | null> {
  if (restored.value) return current.value;
  if (!supabaseConfigured()) { restored.value = true; return null; }
  checking.value = true;
  try {
    const supabase = await getSupabase();
    if (!supabase) return null;
    const { data } = await supabase.auth.getSession();
    current.value = data.session?.user ? toAccount(data.session.user) : null;

    if (!watching) {
      watching = true;
      // The tab that signed out is not necessarily the tab looking at the button.
      supabase.auth.onAuthStateChange((_event, session: Session | null) => {
        current.value = session?.user ? toAccount(session.user) : null;
      });
    }
    return current.value;
  } catch {
    return null;
  } finally {
    checking.value = false;
    restored.value = true;
  }
}

// ── Failures, named ────────────────────────────────────────────────────────────────────────

export type Trouble =
  | 'no-credentials'
  | 'unreachable'
  | 'bad-email'
  | 'short-password'
  | 'wrong'
  | 'unconfirmed'
  | 'exists'
  | 'weak'
  | 'provider-off'
  | 'signups-off'
  | 'rate-limited'
  | 'refused';

/**
 * A sentence per failure, and one is written for every case rather than a default.
 *
 * None of these carries a raw server message. Supabase's prose has changed between releases, it
 * is not written for this app's reader, and a thrown fetch's message can carry the request URL —
 * which carries the key. `code` is the stable identifier and it is what this switches on.
 */
export const explainTrouble = (trouble: Trouble): string => ({
  'no-credentials': 'This build has no server configured, so there is nothing to sign in to.',
  'unreachable': 'The server did not answer. Nothing you have done is lost.',
  'bad-email': 'That does not look like an email address.',
  'short-password': 'A password needs at least eight characters.',
  'wrong': 'That address and password do not match an account.',
  'unconfirmed': 'The account exists but its address has not been confirmed yet.',
  'exists': 'There is already an account for that address.',
  'weak': 'The server refused that password as too weak.',
  'provider-off': 'Email sign-in is switched off in this Supabase project.',
  'signups-off': 'This project is not accepting new accounts, so you need one that already exists.',
  'rate-limited': 'The server is rate limiting sign-in mail. Wait a minute and try again.',
  'refused': 'The server refused the sign-in and did not say why.',
}[trouble]);

/** Map an auth error onto one of the cases above. Pure, and the whole reason each case exists. */
export function classify(error: { code?: string; status?: number } | null): Trouble {
  if (!error) return 'refused';
  const code = error.code ?? '';
  if (code === 'invalid_credentials') return 'wrong';
  if (code === 'email_not_confirmed') return 'unconfirmed';
  if (code === 'user_already_exists' || code === 'email_exists') return 'exists';
  if (code === 'weak_password') return 'weak';
  if (code === 'email_provider_disabled') return 'provider-off';
  if (code === 'signup_disabled') return 'signups-off';
  if (code === 'over_email_send_rate_limit' || code === 'over_request_rate_limit') return 'rate-limited';
  if (code === 'validation_failed' || code === 'email_address_invalid') return 'bad-email';
  // Status 0 or absent is a fetch that never reached the service, not a service that refused.
  if (error.status === undefined || error.status === 0) return 'unreachable';
  return 'refused';
}

export type Outcome =
  | { ok: true; account: Account }
  /** The account was made, but there is no session until the address is confirmed. */
  | { ok: true; account: null; pending: string }
  | { ok: false; trouble: Trouble };

// ── The pure guards the form uses before it spends a round trip ────────────────────────────

/**
 * Deliberately loose. The only address that is definitely wrong is one that cannot be an address
 * at all; the server is the authority on the rest, and a client-side regex that refuses a valid
 * address is worse than a wasted request.
 */
export const emailLooksValid = (input: string): boolean =>
  /^[^\s@]+@[^\s@.]+\.[^\s@]+$/.test(input.trim());

export const MIN_PASSWORD = 8;
export const passwordLongEnough = (input: string): boolean => input.length >= MIN_PASSWORD;

// ── The three actions ──────────────────────────────────────────────────────────────────────

export async function signIn(email: string, password: string): Promise<Outcome> {
  if (!emailLooksValid(email)) return { ok: false, trouble: 'bad-email' };
  if (!password) return { ok: false, trouble: 'short-password' };

  const supabase = await getSupabase();
  if (!supabase) return { ok: false, trouble: 'no-credentials' };
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(), password,
    });
    if (error) return { ok: false, trouble: classify(error as { code?: string; status?: number }) };
    if (!data.user) return { ok: false, trouble: 'refused' };
    current.value = toAccount(data.user);
    rememberEntry('account');
    return { ok: true, account: current.value };
  } catch {
    return { ok: false, trouble: 'unreachable' };
  }
}

/**
 * Make an account.
 *
 * Two different successes, and conflating them is the bug this signature exists to prevent: with
 * `mailer_autoconfirm` on, `signUp` returns a session and the person is in; with it off — which
 * is this project — it returns a user and NO session, and the honest report is "check your mail",
 * not "you are signed in". A UI that reads `!error` as success shows a signed-in state that the
 * next reload takes away.
 */
export async function createAccount(email: string, password: string): Promise<Outcome> {
  if (!emailLooksValid(email)) return { ok: false, trouble: 'bad-email' };
  if (!passwordLongEnough(password)) return { ok: false, trouble: 'short-password' };

  const supabase = await getSupabase();
  if (!supabase) return { ok: false, trouble: 'no-credentials' };
  try {
    const { data, error } = await supabase.auth.signUp({ email: email.trim(), password });
    if (error) return { ok: false, trouble: classify(error as { code?: string; status?: number }) };
    // The device has answered the entry screen either way: an account exists now, and it exists
    // alongside every sheet already here rather than instead of them.
    rememberEntry('account');
    if (data.session?.user) {
      current.value = toAccount(data.session.user);
      return { ok: true, account: current.value };
    }
    return { ok: true, account: null, pending: email.trim() };
  } catch {
    return { ok: false, trouble: 'unreachable' };
  }
}

/**
 * Mail a reset link.
 *
 * The confirmation says "if there is an account", and that is not vagueness: answering differently
 * for a known and an unknown address turns this form into a list of who has an account here.
 */
export async function sendReset(email: string): Promise<Outcome> {
  if (!emailLooksValid(email)) return { ok: false, trouble: 'bad-email' };
  const supabase = await getSupabase();
  if (!supabase) return { ok: false, trouble: 'no-credentials' };
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/signin`,
    });
    if (error) return { ok: false, trouble: classify(error as { code?: string; status?: number }) };
    return { ok: true, account: null, pending: email.trim() };
  } catch {
    return { ok: false, trouble: 'unreachable' };
  }
}

export async function signOut(): Promise<void> {
  const supabase = await getSupabase();
  current.value = null;
  if (!supabase) return;
  try { await supabase.auth.signOut(); } catch { /* the local session is already forgotten */ }
}

/** Test seam: forget everything this module cached. */
/** Forget a failed probe now, rather than after `RETRY_MS`. What a returning network presses. */
export function forgetProbeFailure(): void {
  if (probed && !probed.ok && probed.reason === 'unreachable') {
    probed = null;
    failedAt = 0;
  }
}

export function resetAuth(): void {
  probed = null;
  failedAt = 0;
  current.value = null;
  restored.value = false;
  checking.value = false;
}
